import { colors } from '../../../theme/tokens/colors';
import { spacingFunction } from '../../../theme/tokens/spacing';
import { createLicenseSx } from '../../license-management/styles/licenseLayout';
import { spacing } from '../../../theme/tokens';

const FILTER_CONTROL_HEIGHT = 34;

export function createTemplateSx(tokenOverrides = {}) {
  const base = createLicenseSx(tokenOverrides);
  const outlinedInput = base.searchField?.['& .MuiOutlinedInput-root'] ?? {};

  const sharedOutlinedField = {
    ...outlinedInput,
    height: FILTER_CONTROL_HEIGHT,
    minHeight: FILTER_CONTROL_HEIGHT,
    bgcolor: colors.white,
    fontSize: '0.8125rem',
    '& .MuiAutocomplete-input, & .MuiInputBase-input': {
      py: '0 !important',
      fontSize: '0.8125rem',
    },
    '& .MuiInputAdornment-root': {
      mr: 0.25,
    },
  };

  return {
    ...base,
    pageRoot: {
      ...base.pageRoot,
      px: { xs: 1.5, md: 2.5 },
      py: { xs: 1.5, md: 2 },
      minWidth: 0,
      maxWidth: '100%',
    },
    pageHeader: {
      ...base.pageHeader,
      mb: 1.5,
      gap: 1.5,
    },
    pageTitle: {
      ...base.pageTitle,
      fontSize: '1.7143rem',
      lineHeight: 1.2,
      fontWeight:600
    },
    pageSubtitle: {
      ...base.pageSubtitle,
      fontSize:'0.9286rem',
      mt: 0.25,
    },
    countBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 28,
      height: 22,
      px: 0.75,
      borderRadius: 999,
      bgcolor: colors.grey[200],
      color: colors.grey[700],
      fontSize: '0.75rem',
      fontWeight: 700,
      lineHeight: 1,
    },
    createButton: {
      ...base.createButton,
      minHeight: 34,
      py: 0.5,
      px: 1.75,
      fontSize: '0.8125rem',
      borderRadius: 1,
    },
    syncButton: {
      textTransform: 'none',
      minHeight: 34,
      py: 0.5,
      px: 1.5,
      borderRadius: 1,
      fontSize: '0.8125rem',
      borderColor: colors.grey[300],
      color: colors.grey[700],
      '&:hover': {
        borderColor: colors.grey[400],
        bgcolor: colors.grey[50],
      },
    },
    syncAlert: {
      mb: 1.5,
      px: 1.25,
      py: 0.75,
      borderRadius: 1,
      fontSize: '0.8125rem',
    },
    dashboardSurface: {
      border: `1px solid ${colors.grey[200]}`,
      borderRadius: 1.5,
      bgcolor: colors.white,
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
      overflow: 'hidden',
    },
    statusTabsBar: {
      display: 'flex',
      alignItems: 'stretch',
      flexWrap: { xs: 'wrap', md: 'nowrap' },
      gap: { xs: 1, md: 0 },
      width: '100%',
      mb: 0,
      px: { xs: 1, sm: 1.5 },
      pt: { xs: 1, sm: 0 },
      pb: { xs: 1, md: 0 },
      borderBottom: `1px solid ${colors.grey[200]}`,
      bgcolor: colors.white,
    },
    statusTabsMain: {
      flex: '1 1 auto',
      minWidth: 0,
    },
    statusTabsOptinSlot: {
      display: 'flex',
      alignItems: 'center',
      flexShrink: 0,
      pl: { xs: 0, md: 1.5 },
      pr: { xs: 0, sm: 0.5 },
      pb: { xs: 0.25, md: 1 },
      pt: { xs: 0.25, md: 1 },
      borderLeft: { xs: 'none', md: `1px solid ${colors.grey[200]}` },
      width: { xs: '100%', md: 'auto' },
      justifyContent: { xs: 'flex-start', md: 'center' },
    },
    statusTabsWrapper: {
      width: '100%',
      mb: 0,
      px: { xs: 1, sm: 1.5 },
      pt: { xs: 0.5, sm: 0.75 },
      borderBottom: `1px solid ${colors.grey[200]}`,
      bgcolor: colors.white,
    },
    optinUsersTabButton: (active) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      px: 1.5,
      py: 0.875,
      borderRadius: '4px',
      border: `1px solid ${active ? colors.primary?.main ?? '#16A34A' : colors.grey[300]}`,
      bgcolor: active ? 'rgba(22, 163, 74, 0.08)' : colors.grey[50],
      color: active ? 'primary.main' : colors.grey[700],
      transition: 'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
      boxShadow: active ? '0 0 0 1px rgba(22, 163, 74, 0.12)' : 'none',
      '&:hover': {
        bgcolor: active ? 'rgba(22, 163, 74, 0.12)' : colors.grey[100],
        borderColor: active ? 'primary.main' : colors.grey[400],
      },
    }),
    optinUsersTabIcon: (active) => ({
      fontSize: '1.125rem',
      color: active ? 'primary.main' : colors.grey[600],
    }),
    optinUsersTabLabel: (active) => ({
      fontSize: '0.8125rem',
      fontWeight: 700,
      lineHeight: 1.2,
      color: active ? 'primary.main' : colors.grey[800],
      whiteSpace: 'nowrap',
    }),
    optinUsersTabBadge: (active) => ({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 24,
      height: 20,
      px: 0.625,
      borderRadius: 999,
      bgcolor: active ? 'rgba(22, 163, 74, 0.18)' : colors.grey[200],
      color: active ? '#166534' : colors.grey[700],
      fontSize: '0.6875rem',
      fontWeight: 700,
      lineHeight: 1,
    }),
    optinUsersToolbar: {
      width: '100%',
      px: { xs: 1, sm: 1.5 },
      py: 1.25,
      borderBottom: `1px solid ${colors.grey[100]}`,
      bgcolor: colors.white,
    },
    optinUsersToolbarInner: {
      display: 'flex',
      alignItems: { xs: 'flex-start', sm: 'center' },
      justifyContent: 'space-between',
      gap: 1.5,
      flexWrap: 'wrap',
      width: '100%',
    },
    optinUsersToolbarActions: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
      flexWrap: 'nowrap',
      gap: 1,
      flexShrink: 0,
      ml: { xs: 0, sm: 'auto' },
      width: { xs: '100%', sm: 'auto' },
      justifyContent: { xs: 'flex-end', sm: 'flex-end' },
    },
    optinUsersTableWrap: {
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      px: { xs: 1, sm: 1.5 },
      pt: 0,
      pb: { xs: 1, sm: 1.5 },
    },
    optinUsersPaginationBar: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      width: '100%',
      px: { xs: 0.5, sm: 1 },
      py: 1,
      borderTop: `1px solid ${colors.grey[100]}`,
      bgcolor: colors.white,
    },
    statusTabs: {
      minHeight: 40,
      '& .MuiTab-root': {
        minHeight: 40,
        py: 0.75,
        px: { xs: 1.25, sm: 2 },
        fontSize: '0.8125rem',
        fontWeight: 600,
        textTransform: 'none',
        color: colors.grey[600],
        '&.Mui-selected': {
          color: 'primary.main',
        },
      },
      '& .MuiTabs-indicator': {
        height: 2,
        borderRadius: '2px 2px 0 0',
        bgcolor: 'primary.main',
      },
    },
    statusTabLabel: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
    },
    statusTabText: {
      fontSize: '0.8125rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    statusTabBadge: (variant) => {
      const palette = {
        neutral: { bg: colors.grey[200], color: colors.grey[700] },
        success: { bg: '#DCFCE7', color: '#166534' },
        warning: { bg: '#FEF3C7', color: '#92400E' },
        danger: { bg: '#FEE2E2', color: '#B91C1C' },
      };
      const tone = palette[variant] ?? palette.neutral;
      return {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 24,
        height: 20,
        px: 0.625,
        borderRadius: 999,
        bgcolor: tone.bg,
        color: tone.color,
        fontSize: '0.6875rem',
        fontWeight: 700,
        lineHeight: 1,
      };
    },
    filtersToolbar: {
      width: '100%',
      mb: 0,
      px: { xs: 1, sm: 1.5 },
      py: 1.25,
      borderBottom: `1px solid ${colors.grey[100]}`,
      bgcolor: colors.white,
    },
    wabaAppsToolbar: {
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      flexWrap: 'wrap',
      width: '100%',
      mb: 2,
    },
    wabaAppsSearch: {
      width: { xs: '100%', sm: 320, md: 360 },
      maxWidth: '100%',
      flexShrink: 0,
      '& > div:last-of-type': {
        height: 40,
        bgcolor: colors.white,
        borderColor: colors.grey[200],
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        transition: 'border-color 140ms ease, box-shadow 140ms ease',
        '&:focus-within': {
          boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.08)',
        },
      },
    },
    wabaAppsSurface: {
      border: `1px solid ${colors.grey[200]}`,
      borderRadius: 1.5,
      bgcolor: colors.white,
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
      minHeight: 320,
      p: { xs: 1.5, md: 2 },
    },
    wabaAppsGrid: {
      width: '100%',
    },
    wabaProfileCard: {
      p: { xs: 2, md: 3 },
      borderRadius: 1.5,
      bgcolor: colors.white,
      border: `1px solid ${colors.grey[200]}`,
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
    },
    wabaProfileHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 2,
      flexWrap: 'wrap',
    },
    wabaProfileTitle: {
      fontWeight: 700,
      fontSize: '1.25rem',
      lineHeight: 1.2,
    },
    wabaProfileDisplayGrid: {
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
      gap: 2,
    },
    wabaProfileFormGrid: {
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
      gap: 2,
    },
    wabaProfileActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: 1,
      mt: 3,
      pt: 2,
      borderTop: `1px solid ${colors.grey[100]}`,
    },
    filtersBar: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      flexWrap: 'wrap',
      width: '100%',
    },
    activeFilterChipsRow: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 0.75,
      mt: 1,
    },
    activeFilterChip: {
      height: 26,
      fontSize: '0.75rem',
      bgcolor: colors.white,
      border: `1px solid ${colors.grey[300]}`,
      '& .MuiChip-deleteIcon': {
        fontSize: 16,
        color: colors.grey[500],
        '&:hover': {
          color: colors.grey[700],
        },
      },
    },
    filterClearAllLink: {
      textTransform: 'none',
      fontSize: '0.8125rem',
      fontWeight: 600,
      color: 'primary.main',
      minHeight: 26,
      px: 0.5,
      '&:hover': {
        bgcolor: 'transparent',
        textDecoration: 'underline',
      },
    },
    bulkSelectionBar: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      flexWrap: 'wrap',
      mb: 1,
      px: 1.25,
      py: 0.75,
      borderRadius: 1,
      border: `1px solid ${colors.grey[200]}`,
      bgcolor: '#F8FAFC',
    },
    filterToggleButton: {
      textTransform: 'none',
      whiteSpace: 'nowrap',
      minHeight: FILTER_CONTROL_HEIGHT,
      height: FILTER_CONTROL_HEIGHT,
      fontSize: '0.8125rem',
      px: 1.5,
      borderRadius: 1,
      boxShadow: 'none',
      '& .MuiButton-startIcon': {
        mr: 0.5,
        '& > svg': { fontSize: 18 },
      },
    },
    filterClearLinkButton: {
      textTransform: 'none',
      fontSize: '0.8125rem',
      color: colors.grey[600],
      minHeight: FILTER_CONTROL_HEIGHT,
      '&:hover': {
        bgcolor: 'transparent',
        color: colors.grey[800],
      },
    },
    filterPopoverPaper: {
      width: { xs: 'min(92vw, 420px)', sm: 440 },
      mt: 0.75,
      borderRadius: 1.5,
      border: `1px solid ${colors.grey[200]}`,
      boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
    },
    filterPopoverContent: {
      p: 2,
    },
    filterPopoverHeader: {
      mb: 1.5,
    },
    filterPopoverTitle: {
      fontSize: '0.9375rem',
      fontWeight: 700,
      color: colors.grey[900],
    },
    filterPopoverSubtitle: {
      fontSize: '0.75rem',
      color: colors.grey[500],
      mt: 0.25,
    },
    filterPopoverGrid: {
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
      gap: 1.5,
    },
    filterPopoverActions: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
    },
    filterPopoverResetButton: {
      textTransform: 'none',
      fontSize: '0.8125rem',
      color: colors.grey[600],
    },
    filtersGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      flex: 1,
      flexWrap: { xs: 'wrap', lg: 'nowrap' },
      minWidth: 0,
    },
    filterSearch: {
      flex: '0 0 auto',
      width: { xs: '100%', sm: 320 },
      maxWidth: { xs: '100%', sm: 360 },
      '& .MuiOutlinedInput-root': {
        ...sharedOutlinedField,
        borderRadius: 1,
        '& fieldset': {
          borderColor: colors.grey[300],
        },
      },
    },
    filterControl: {
      width: '100%',
      m: 0,
      '& .MuiOutlinedInput-root': {
        ...sharedOutlinedField,
        borderRadius: 1,
        '& fieldset': {
          borderColor: colors.grey[300],
        },
      },
      '& .MuiAutocomplete-input': {
        minWidth: '0 !important',
        textOverflow: 'clip',
        overflow: 'visible',
      },
      '& .MuiAutocomplete-inputRoot': {
        flexWrap: 'nowrap',
      },
      '& .MuiFormHelperText-root': {
        display: 'none',
      },
    },
    filterField: {
      flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', lg: '0 0 auto' },
      minWidth: { xs: 0, sm: 0 },
      flexShrink: { lg: 0 },
    },
    filterOrgField: {
      width: { lg: 160 },
      minWidth: { lg: 160 },
    },
    filterBotField: {
      width: { lg: 150 },
      minWidth: { lg: 150 },
    },
    filterCategoryField: {
      width: { lg: 168 },
      minWidth: { lg: 168 },
    },
    filterTagField: {
      width: { lg: 120 },
      minWidth: { lg: 120 },
    },
    filterLanguageField: {
      width: { lg: 120 },
      minWidth: { lg: 120 },
    },
    filterStatusField: {
      width: { lg: 132 },
      minWidth: { lg: 132 },
    },
    templateUsersStatusField: {
      width: { xs: '100%', sm: 200, lg: 220 },
      minWidth: { sm: 200, lg: 220 },
    },
    filterArchiveField: {
      width: { lg: 128 },
      minWidth: { lg: 128 },
    },
    filterClearButton: {
      textTransform: 'none',
      whiteSpace: 'nowrap',
      flexShrink: 0,
      minHeight: FILTER_CONTROL_HEIGHT,
      height: FILTER_CONTROL_HEIGHT,
      fontSize: '0.8125rem',
      borderColor: colors.grey[300],
      color: colors.grey[700],
      bgcolor: colors.white,
      px: 1.25,
      py: 0,
      borderRadius: 1,
      '& .MuiButton-startIcon': {
        mr: 0.5,
        '& > svg': { fontSize: 16 },
      },
      '&:hover': {
        borderColor: colors.grey[400],
        bgcolor: colors.white,
      },
    },
    tablePaper: {
      overflow: 'hidden',
      maxWidth: '100%',
      border: 'none',
      borderRadius: 0,
      boxShadow: 'none',
      bgcolor: colors.white,
      '& .MuiTableContainer-root': {
        width: '100%',
        maxWidth: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
      },
      '& [data-pinned="right"]': {
        boxShadow: '-6px 0 10px rgba(15, 23, 42, 0.06)',
      },
      '& [data-pinned="right"]:before': {
        bgcolor: colors.white,
      },
      '& .MuiTableBody-root .MuiTableCell-root': {
        bgcolor: colors.white,
        backgroundColor: colors.white,
      },
      '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even) .MuiTableCell-root': {
        bgcolor: colors.white,
        backgroundColor: colors.white,
      },
      '& .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-root': {
        bgcolor: colors.grey[50],
        backgroundColor: colors.grey[50],
      },
      '& .MuiTableCell-root[data-pinned="true"]': {
        bgcolor: `${colors.white} !important`,
        backgroundColor: `${colors.white} !important`,
        boxShadow: 'none !important',
      },
      '& .MuiTableRow-root:hover .MuiTableCell-root[data-pinned="true"]': {
        bgcolor: `${colors.grey[50]} !important`,
        backgroundColor: `${colors.grey[50]} !important`,
      },
      '& .MuiTableRow-root': {
        height: 44,
      },
    },
    tableContainer: {
      width: '100%',
      maxWidth: '100%',
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
    },
    tableHeadCell: {
      bgcolor: '#F8FAFC',
      color: colors.grey[700],
      fontWeight: 700,
      fontSize: '0.6875rem',
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      borderBottom: `1px solid ${colors.grey[300]}`,
      verticalAlign: 'middle',
      py: 0.75,
      px: 1.5,
      '&, & *': {
        color: `${colors.grey[700]} !important`,
      },
      '& .MuiTypography-root': {
        color: `${colors.grey[700]} !important`,
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      },
      '& .Mui-TableHeadCell-Content': {
        alignItems: 'center',
      },
      '& .Mui-TableHeadCell-Content-Labels': {
        color: `${colors.grey[700]} !important`,
        alignItems: 'center',
      },
      '& .Mui-TableHeadCell-Content-Wrapper': {
        color: `${colors.grey[700]} !important`,
      },
      '& .MuiTableSortLabel-root': {
        color: `${colors.grey[700]} !important`,
        opacity: 1,
      },
      '& .MuiTableSortLabel-root:hover': {
        color: `${colors.grey[800]} !important`,
      },
      '& .MuiTableSortLabel-root.Mui-active': {
        color: `${colors.grey[800]} !important`,
      },
      '& .MuiTableSortLabel-icon': {
        color: `${colors.grey[600]} !important`,
        opacity: '1 !important',
        fontSize: '1rem',
      },
      '& .Mui-TableHeadCell-Content': {
        justifyContent: 'flex-start',
      },
    },
    tableHeadRow: {
      bgcolor: '#F8FAFC',
      boxShadow: 'none',
    },
    tableBodyCell: {
      fontSize: '0.8125rem',
      color: colors.grey[800],
      borderBottom: `1px solid ${colors.grey[200]}`,
      bgcolor: colors.white,
      textAlign: 'left',
      py: 0.5,
      px: 1.5,
      lineHeight: 1.4,
    },
    tableBodyRow: {
      '&:hover td': {
        bgcolor: colors.grey[50],
      },
    },
    templateNameCell: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 0.875,
      minWidth: 0,
      width: '100%',
    },
    templateNameTextBlock: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      flex: 1,
    },
    templateNameSubtitle: {
      fontSize: '0.6875rem',
      color: colors.grey[500],
      lineHeight: 1.3,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      mt: 0.125,
    },
    templateNameCellWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      minWidth: 0,
      width: '100%',
    },
    templateNameCellHoverActions: {
      '&:hover .template-view-performance, &:focus-within .template-view-performance': {
        display: 'inline-flex',
      },
    },
    templateNameInsightsButton: {
      display: 'none',
      flexShrink: 0,
      alignSelf: 'flex-start',
      minWidth: 0,
      minHeight: 0,
      px: 0.75,
      py: 0.125,
      mt: 0.25,
      ml: -0.5,
      textTransform: 'none',
      fontSize: '0.75rem',
      fontWeight: 600,
      lineHeight: 1.2,
      cursor: 'pointer',
      color: 'primary.main',
      bgcolor: colors.white,
      border: `1px solid ${colors.grey[200]}`,
      borderRadius: 1,
      boxShadow: '0 2px 8px rgba(15, 23, 42, 0.08)',
      '&:hover': {
        bgcolor: colors.grey[50],
        textDecoration: 'underline',
      },
      '& .MuiButton-startIcon': {
        mr: 0.5,
        ml: 0,
        cursor: 'pointer',
      },
      '& .MuiButton-startIcon > *:nth-of-type(1)': {
        fontSize: 15,
      },
      '& *': {
        cursor: 'pointer',
      },
    },
    templateNameAvatar: {
      width: 26,
      height: 26,
      fontSize: '0.6875rem',
      fontWeight: 700,
      flexShrink: 0,
      bgcolor: 'primary.main',
      color: colors.white,
    },
    templateNameLink: {
      fontWeight: 600,
      fontSize: '0.8125rem',
      cursor: 'pointer',
      color: 'primary.main',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      flex: 1,
      minWidth: 0,
    },
    templateNameInfoIcon: {
      p: 0.25,
      color: colors.grey[500],
      flexShrink: 0,
      '&:hover': {
        color: colors.grey[700],
        bgcolor: colors.grey[100],
      },
    },
    rowActionButton: {
      p: 0.375,
      color: colors.grey[600],
      '&:hover': {
        bgcolor: colors.grey[100],
      },
    },
    actionColumnHeadCell: {
      textAlign: 'center',
      px: 1,
      bgcolor: '#F8FAFC',
      '& .Mui-TableHeadCell-Content': {
        justifyContent: 'center',
      },
      '& .Mui-TableHeadCell-Content-Labels': {
        justifyContent: 'center',
      },
    },
    actionColumnBodyCell: {
      textAlign: 'center',
      px: 1,
      py: 0.25,
      bgcolor: colors.white,
      backgroundColor: colors.white,
    },
    actionColumnCell: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
    },
    formPageRoot: {
      ...base.pageRoot,
      minHeight: 0,
      bgcolor: '#F8FAFC',
    },
    formHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 1.5,
      mb: 2.5,
    },
    formBackButton: {
      flexShrink: 0,
      mt: 0.25,
    },
    formTitle: {
      fontWeight: 700,
      fontSize: '1.35rem',
      lineHeight: 1.2,
      color: colors.grey[900],
    },
    formSubtitle: {
      fontSize: '0.8125rem',
      color: colors.grey[600],
      mt: 0.25,
    },
    botContextBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      px: 1.5,
      py: 1,
      mb: 2,
      borderRadius: 1.5,
      border: `1px solid ${colors.grey[200]}`,
      bgcolor: colors.white,
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
    },
    botContextLabel: {
      fontSize: '0.8125rem',
      fontWeight: 600,
      color: colors.grey[800],
    },
    botContextMeta: {
      fontSize: '0.75rem',
      color: colors.grey[600],
      mt: 0.125,
    },
    botContextEditButton: {
      color: colors.grey[600],
      border: `1px solid ${colors.grey[300]}`,
      borderRadius: 1,
      p: 0.5,
      '&:hover': {
        bgcolor: colors.grey[50],
        borderColor: colors.grey[400],
      },
    },
    formSectionCard: {
      border: `1px solid ${colors.grey[200]}`,
      borderRadius: 2,
      bgcolor: colors.white,
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
      mb: 2,
      p: { xs: 2, md: 2.5 },
    },
    formSectionTitle: {
      fontWeight: 700,
      fontSize: '0.9375rem',
      color: colors.grey[900],
      lineHeight: 1.3,
    },
    formSectionDescription: {
      fontSize: '0.8125rem',
      color: colors.grey[500],
      mt: 0.25,
    },
    formSectionBody: {
      p: 0,
    },
    formOptionCards: {
      display: 'grid',
      gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
      gap: 1,
    },
    formOptionCard: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 72,
      py: 1.25,
      px: 1,
      borderRadius: 1.5,
      border: `1px solid ${colors.grey[300]}`,
      bgcolor: colors.white,
      cursor: 'pointer',
      color: colors.grey[700],
      transition: 'all 0.15s ease',
      '&:hover': {
        borderColor: 'primary.main',
        bgcolor: colors.grey[50],
      },
    },
    formOptionCardSelected: {
      borderColor: 'primary.main',
      bgcolor: '#F0FDF4',
      color: colors.grey[900],
      boxShadow: '0 0 0 1px rgba(22, 163, 74, 0.25)',
    },
    formBodyFooter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      mt: 0.75,
      flexWrap: 'wrap',
    },
    formCharCount: {
      fontSize: '0.75rem',
      color: colors.grey[500],
    },
    formCallingCard: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1.5,
      px: { xs: 2, md: 2.5 },
      py: 1.5,
      mb: 2,
      borderRadius: 2,
      border: `1px solid ${colors.grey[200]}`,
      bgcolor: colors.white,
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
    },
    formCallingIconWrap: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 36,
      height: 36,
      borderRadius: 1,
      bgcolor: colors.grey[100],
      color: colors.grey[600],
      flexShrink: 0,
    },
    formCallingTitle: {
      fontSize: '0.875rem',
      fontWeight: 700,
      color: colors.grey[900],
      lineHeight: 1.3,
    },
    formCallingDescription: {
      fontSize: '0.75rem',
      color: colors.grey[500],
      mt: 0.25,
    },
    formField: {
      mb: 0,
      '& .MuiOutlinedInput-notchedOutline': {
        top: spacingFunction(0),
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.8125rem',
        fontWeight: 600,
        color: colors.grey[800],
        mb: 0.5,
        position: 'relative',
        transform: 'none',
      },
      '& .MuiInputLabel-shrink': {
        transform: 'none',
      },
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8125rem',
        bgcolor: colors.white,
        '& fieldset': {
          borderColor: colors.grey[300],
        },
        '&:hover fieldset': {
          borderColor: colors.grey[400],
        },
        '&.Mui-focused fieldset': {
          borderColor: 'primary.main',
          borderWidth: 1,
        },
      },
      '& .MuiFormHelperText-root': {
        fontSize: '0.75rem',
        mx: 0,
        mt: 0.5,
      },
    },
    formFieldSpacing: {
      mb: 2,
      '& .MuiFormLabel-root': {
          top: -spacing.size1,
        },
    },
    formSelectControl: {
      width: '100%',
      m: 0,
      mb: 2,
      '& .MuiOutlinedInput-notchedOutline': {
        top: spacingFunction(0),
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.8125rem',
        fontWeight: 600,
        color: colors.grey[800],
      },
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8125rem',
        bgcolor: colors.white,
        minHeight: 38,
        '& fieldset': {
          borderColor: colors.grey[300],
        },
        '&:hover fieldset': {
          borderColor: colors.grey[400],
        },
        '&.Mui-focused fieldset': {
          borderColor: 'primary.main',
          borderWidth: 1,
        },
      },
      '& .MuiFormHelperText-root': {
        fontSize: '0.75rem',
        mx: 0,
        mt: 0.5,
      },
    },
    formLabel: {
      display: 'block',
      fontSize: '0.8125rem',
      fontWeight: 600,
      color: colors.grey[800],
      mb: 0.75,
    },
    formVariablesBlock: {
      mt: 2,
      p: 1.5,
      borderRadius: 1.5,
      border: `1px dashed ${colors.grey[300]}`,
      bgcolor: colors.grey[50],
    },
    formVariablesTitle: {
      fontSize: '0.8125rem',
      fontWeight: 700,
      color: colors.grey[800],
      mb: 1.25,
    },
    formVariableIndex: {
      fontSize: '0.8125rem',
      fontWeight: 600,
      color: colors.grey[700],
      fontFamily: 'monospace',
    },
    formToggleRow: {
      display: 'flex',
      alignItems: 'center',
      px: 1.5,
      py: 1,
      mb: 2,
      borderRadius: 1.5,
      border: `1px solid ${colors.grey[200]}`,
      bgcolor: colors.white,
    },
    formActionsBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 1.5,
      pt: 1,
      pb: 2,
      mt: 0.5,
    },
    formPreviewSticky: {
      position: { lg: 'sticky' },
      top: { lg: 16 },
    },
    previewCard: {
      border: `1px solid ${colors.grey[200]}`,
      borderRadius: 1.5,
      bgcolor: colors.white,
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
      overflow: 'hidden',
    },
    previewCardHeader: {
      px: 2,
      py: 1.25,
      borderBottom: `1px solid ${colors.grey[100]}`,
      bgcolor: colors.grey[50],
    },
    previewCardTitle: {
      fontSize: '0.875rem',
      fontWeight: 700,
      color: colors.grey[900],
    },
    previewCardSubtitle: {
      fontSize: '0.75rem',
      color: colors.grey[600],
      mt: 0.25,
    },
    previewMetaBlock: {
      mt: 1.25,
      pt: 1.25,
      borderTop: `1px solid ${colors.grey[200]}`,
    },
    previewMetaChips: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 0.75,
      mt: 0.75,
    },
    previewMetaChip: {
      fontSize: '0.6875rem',
      height: 24,
    },
    previewCardBody: {
      p: 2,
    },
    previewTemplateName: {
      fontSize: '0.8125rem',
      fontWeight: 600,
      color: colors.grey[800],
      wordBreak: 'break-word',
      lineHeight: 1.35,
    },
    previewPhoneShell: {
      borderRadius: 1.5,
      overflow: 'hidden',
      border: `1px solid ${colors.grey[200]}`,
    },
    previewPhoneHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      bgcolor: '#128C7E',
      color: colors.white,
      px: 1.5,
      py: 0.875,
    },
    previewPhoneHeaderTitle: {
      fontSize: '0.75rem',
      fontWeight: 700,
    },
    previewPhoneHeaderTime: {
      fontSize: '0.6875rem',
      opacity: 0.9,
    },
    previewBubble: {
      bgcolor: '#dcf8c6',
      p: 1.5,
      minHeight: 80,
    },
    previewBubbleText: {
      fontSize: '0.8125rem',
      color: colors.grey[900],
      whiteSpace: 'pre-line',
      wordBreak: 'break-word',
      lineHeight: 1.45,
    },
    previewFooterText: {
      mt: 1.25,
      pt: 1,
      fontSize: '0.6875rem',
      color: colors.grey[600],
      borderTop: `1px dashed ${colors.grey[400]}`,
    },
    previewButtonsRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 0.75,
      mt: 1.25,
    },
    previewButtonChip: {
      fontSize: '0.6875rem',
      minWidth: 72,
      py: 0.375,
      px: 1,
      color: '#128C7E',
      borderColor: '#128C7E',
    },
    sendDialogTabs: {
      minHeight: 40,
      mb: 2,
      borderBottom: `1px solid ${colors.grey[200]}`,
      '& .MuiTab-root': {
        minHeight: 40,
        py: 0.75,
        px: 1.5,
        fontSize: '0.8125rem',
        fontWeight: 600,
        textTransform: 'none',
      },
    },
    sendDialogSection: {
      p: 2,
      borderRadius: 1.5,
      border: `1px solid ${colors.grey[200]}`,
      bgcolor: colors.white,
    },
    sendDialogSectionTitle: {
      fontSize: '0.8125rem',
      fontWeight: 700,
      color: colors.grey[800],
      mb: 1.5,
    },
    sendDialogSectionHint: {
      fontSize: '0.75rem',
      color: colors.grey[600],
      mb: 1.5,
    },
    sendDialogGrid: {
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' },
      gap: 2,
      alignItems: 'start',
    },
    sendDialogActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: 1,
      pt: 2,
      mt: 2,
      borderTop: `1px solid ${colors.grey[200]}`,
    },
    sendDialogBulkRow: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 1,
    },
    sendDialogStatRow: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 1.5,
      mt: 2,
      p: 1.25,
      borderRadius: 1,
      bgcolor: colors.grey[50],
      border: `1px solid ${colors.grey[200]}`,
    },
  };
}
